import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, Check, UserPlus } from "@phosphor-icons/react";

const TYPE_ICON = { registration: UserPlus };

export default function NotificationBell({ variant = "sidebar" }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  async function load() {
    try {
      const [c, l] = await Promise.all([
        api.get("/notifications/count"),
        api.get("/notifications?limit=10"),
      ]);
      setCount(c.data?.count || 0);
      setItems(l.data || []);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onUpdate = () => load();
    window.addEventListener("wahdat:notifications-refresh", onUpdate);
    return () => { clearInterval(t); window.removeEventListener("wahdat:notifications-refresh", onUpdate); };
  }, []);

  useEffect(() => {
    function onDoc(e) { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function openItem(n) {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    }
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  }

  async function markAllRead() {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch { /* noop */ }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        data-testid="notification-bell"
        aria-label="Notifications"
        className={
          variant === "topbar"
            ? "p-2 rounded-md hover:bg-neutral-900 relative"
            : "w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold text-neutral-400 hover:text-white hover:bg-neutral-900 relative"
        }
      >
        <Bell size={20} weight="bold" />
        {variant === "sidebar" && <span className="flex-1 text-left">Notifications</span>}
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className={
              variant === "topbar"
                ? "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D90429] text-white text-[10px] font-black flex items-center justify-center"
                : "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D90429] text-white text-[10px] font-black"
            }
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className={
            variant === "topbar"
              ? "absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-[#0a0a0a] border border-neutral-800 rounded-lg shadow-2xl z-50 overflow-hidden"
              : "absolute left-full ml-2 bottom-0 w-80 bg-[#0a0a0a] border border-neutral-800 rounded-lg shadow-2xl z-50 overflow-hidden"
          }
        >
          <div className="flex items-center justify-between p-3 border-b border-neutral-900">
            <div className="overline">Notifications</div>
            {count > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notif-mark-all-read"
                className="text-[11px] font-bold uppercase tracking-wider text-[#06D6A0] hover:text-white inline-flex items-center gap-1"
              >
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-sm text-neutral-500 text-center">You're all caught up.</div>
            ) : (
              items.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left px-3 py-3 border-b border-neutral-900 hover:bg-neutral-900 flex gap-3 ${n.read ? "opacity-70" : ""}`}
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${n.read ? "bg-neutral-900 text-neutral-500" : "bg-[#D90429]/15 text-[#D90429]"}`}>
                      <Icon size={18} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{n.title}</div>
                      <div className="text-xs text-neutral-400 line-clamp-2">{n.message}</div>
                      <div className="overline mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#D90429] mt-2 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
